import {
  CanAccess,
  RecordContextProvider,
  required,
  useCanAccess,
  useCreatePath,
  useListContext,
  useRecordContext,
  useStore,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Columns3, List as ListIcon } from "lucide-react";
import { Link } from "react-router";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { Create } from "@/components/admin/create";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { DateInput } from "@/components/admin/date-input";
import { Edit } from "@/components/admin/edit";
import { EditButton } from "@/components/admin/edit-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { NumberInput } from "@/components/admin/number-input";
import { RecordField } from "@/components/admin/record-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectField } from "@/components/admin/select-field";
import { SelectInput } from "@/components/admin/select-input";
import { Show } from "@/components/admin/show";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextField } from "@/components/admin/text-field";
import { TextInput } from "@/components/admin/text-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { SimpleList } from "../simple-list/SimpleList";
import { IthnkPage } from "./IthnkPage";
import { StatusBoard } from "./StatusBoard";
import {
  formatAmount,
  type Invoice,
  invoiceStatusChoices,
  isOverdue,
  overdueFilter,
} from "./ithnkUtils";

const filters = [
  <SelectInput
    key="status"
    source="status"
    choices={invoiceStatusChoices}
    alwaysOn
  />,
];

const useInvoiceView = () =>
  useStore<"board" | "list">("invoices.view", "board");

const ViewToggle = () => {
  const [view, setView] = useInvoiceView();
  const translate = useTranslate();
  const next = view === "board" ? "list" : "board";
  return (
    <Button
      variant="outline"
      size="icon"
      title={translate(`resources.invoices.${next}`)}
      onClick={() => setView(next)}
    >
      {next === "board" ? <Columns3 /> : <ListIcon />}
    </Button>
  );
};

const InvoiceListActions = () => (
  <div className="flex items-center gap-2">
    <ViewToggle />
    <ToggleFilterButton
      className="w-auto"
      label="resources.invoices.overdue"
      value={overdueFilter()}
    />
    <ExportButton />
    <CanAccess action="create">
      <CreateButton />
    </CanAccess>
  </div>
);

/** Status, plus an Overdue badge computed from the due date */
export const InvoiceStatusField = () => {
  const record = useRecordContext<Invoice>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <span className="inline-flex items-center gap-2">
      <SelectField source="status" choices={invoiceStatusChoices} />
      {isOverdue(record) ? (
        <Badge variant="destructive">
          {translate("resources.invoices.overdue")}
        </Badge>
      ) : null}
    </span>
  );
};

export const InvoiceAmountField = (_props: { label?: string }) => {
  const record = useRecordContext<Invoice>();
  if (!record) return null;
  return (
    <span>
      {formatAmount(record.amount_paid, record.currency)} /{" "}
      {formatAmount(record.amount, record.currency)}
    </span>
  );
};

/** An invoice as a board card, linking to its page */
export const InvoiceCard = () => {
  const record = useRecordContext<Invoice>();
  const createPath = useCreatePath();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <Link
      to={createPath({ resource: "invoices", id: record.id, type: "show" })}
    >
      <Card className="py-3 shadow-sm hover:shadow-md">
        <CardContent className="px-3 flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {record.number}
            {isOverdue(record) ? (
              <Badge variant="destructive" className="ml-2">
                {translate("resources.invoices.overdue")}
              </Badge>
            ) : null}
          </span>
          <span className="text-xs text-muted-foreground">
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
            />
          </span>
          <span className="text-xs">
            <InvoiceAmountField />
          </span>
        </CardContent>
      </Card>
    </Link>
  );
};

/** One column per status; drag a card to change its status */
const InvoiceBoard = () => {
  const { data = [] } = useListContext<Invoice>();
  const [update] = useUpdate<Invoice>();
  const { canAccess } = useCanAccess({ resource: "invoices", action: "edit" });
  const translate = useTranslate();
  return (
    <StatusBoard
      disabled={!canAccess}
      columns={invoiceStatusChoices.map((choice) => ({
        id: choice.id,
        label: translate(choice.name),
      }))}
      items={data.map((invoice) => ({
        id: String(invoice.id),
        column: invoice.status,
        content: (
          <RecordContextProvider value={invoice}>
            <InvoiceCard />
          </RecordContextProvider>
        ),
      }))}
      onMove={(id, status) => {
        const invoice = data.find((item) => String(item.id) === id);
        if (!invoice) return;
        update(
          "invoices",
          {
            id: invoice.id,
            data: { status: status as Invoice["status"] },
            previousData: invoice,
          },
          { mutationMode: "optimistic" },
        );
      }}
    />
  );
};

export const InvoiceList = () => {
  const isMobile = useIsMobile();
  const [view] = useInvoiceView();
  return (
    <IthnkPage title="resources.invoices.name">
      <List
        title={isMobile ? false : undefined}
        disableBreadcrumb={isMobile}
        sort={{ field: "due_date", order: "ASC" }}
        perPage={50}
        filters={filters}
        actions={<InvoiceListActions />}
      >
        {view === "board" ? (
          <InvoiceBoard />
        ) : isMobile ? (
          <SimpleList
            rowClick="show"
            primaryText={(record) => record.number}
            secondaryText={<InvoiceStatusField />}
            tertiaryText={<InvoiceAmountField />}
          />
        ) : (
          <DataTable rowClick="show" bulkActionButtons={false}>
            <DataTable.Col source="number" />
            <DataTable.Col source="company_id">
              <ReferenceField source="company_id" reference="companies" />
            </DataTable.Col>
            <DataTable.Col source="due_date" field={DateField} />
            <DataTable.Col source="amount">
              <InvoiceAmountField />
            </DataTable.Col>
            <DataTable.Col source="status" field={InvoiceStatusField} />
          </DataTable>
        )}
      </List>
    </IthnkPage>
  );
};

const InvoiceInputs = () => (
  <>
    <TextInput source="number" validate={required()} />
    <ReferenceInput source="company_id" reference="companies">
      <AutocompleteInput validate={required()} />
    </ReferenceInput>
    <ReferenceInput source="deal_id" reference="deals">
      <AutocompleteInput />
    </ReferenceInput>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <DateInput source="issue_date" />
      <DateInput source="due_date" />
      <NumberInput source="amount" validate={required()} min={0} step={0.01} />
      <TextInput source="currency" defaultValue="EGP" validate={required()} />
      <NumberInput source="amount_paid" defaultValue={0} min={0} step={0.01} />
      <SelectInput
        source="status"
        choices={invoiceStatusChoices}
        defaultValue="draft"
        validate={required()}
      />
    </div>
    <ReferenceInput source="collected_by" reference="sales">
      <AutocompleteInput />
    </ReferenceInput>
    <TextInput source="notes" multiline />
  </>
);

export const InvoiceCreate = () => (
  <IthnkPage title="resources.invoices.name">
    <Create redirect="show">
      <SimpleForm>
        <InvoiceInputs />
      </SimpleForm>
    </Create>
  </IthnkPage>
);

export const InvoiceEdit = () => (
  <IthnkPage title="resources.invoices.name">
    <Edit redirect="show">
      <SimpleForm>
        <InvoiceInputs />
      </SimpleForm>
    </Edit>
  </IthnkPage>
);

export const InvoiceShow = () => (
  <IthnkPage title="resources.invoices.name">
    <Show
      actions={
        <CanAccess action="edit">
          <EditButton />
        </CanAccess>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecordField source="status">
          <InvoiceStatusField />
        </RecordField>
        <RecordField source="amount">
          <InvoiceAmountField />
        </RecordField>
        <RecordField source="company_id">
          <ReferenceField source="company_id" reference="companies" />
        </RecordField>
        <RecordField source="deal_id">
          <ReferenceField source="deal_id" reference="deals" link={false}>
            <TextField source="name" />
          </ReferenceField>
        </RecordField>
        <RecordField source="milestone_id">
          <ReferenceField
            source="milestone_id"
            reference="delivery_milestones"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        </RecordField>
        <RecordField source="issue_date">
          <DateField source="issue_date" />
        </RecordField>
        <RecordField source="due_date">
          <DateField source="due_date" />
        </RecordField>
        <RecordField source="collected_by">
          <ReferenceField source="collected_by" reference="sales" />
        </RecordField>
        <RecordField
          source="notes"
          className="md:col-span-2 whitespace-pre-line"
        />
      </div>
    </Show>
  </IthnkPage>
);
